import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useStudio } from "../lib/context";
import { ErrorBox, Loading } from "./ui";

const pct = (x: number | null) => (x == null ? "—" : `${Math.round(x * 100)} %`);
const sec = (ms: number | null) => (ms == null ? "—" : `${(ms / 1000).toFixed(1).replace(".", ",")} s`);

/** Comment les élèves vivent chaque carte : de quoi repérer celle qui coince (sautée, ratée, « pas compris »). */
export function CardStatsPanel({ notionId }: { notionId: string }) {
  const { languages } = useStudio();
  const langs = languages.map((l) => l.code);
  return (
    <div className="stack" style={{ gap: 16 }}>
      {langs.map((lang) => <LangStats key={lang} notionId={notionId} lang={lang} label={languages.find((l) => l.code === lang)?.nativeName ?? lang} />)}
    </div>
  );
}

function LangStats({ notionId, lang, label }: { notionId: string; lang: string; label: string }) {
  const q = useQuery({ queryKey: ["card-stats", notionId, lang], queryFn: () => api.cardStats(notionId, lang), staleTime: 60_000 });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const s = q.data!;
  if (s.version == null) return <div className="card muted">{label} : aucune version publiée.</div>;
  const worst = (k: "skipRate" | "quizSuccess") => s.cards.filter((c) => c[k] != null).sort((a, b) => (k === "skipRate" ? (b[k]! - a[k]!) : (a[k]! - b[k]!)))[0];
  const skip = worst("skipRate");
  return (
    <div className="card stack appear">
      <div className="row" style={{ flexWrap: "wrap" }}>
        <h2>{label} · version {s.version}</h2>
        <span className="muted small">30 derniers jours · {s.visits} visite{s.visits > 1 ? "s" : ""} · {s.visits ? Math.round((s.completed / s.visits) * 100) : 0} % terminées
          {Object.keys(s.byMode).length > 0 && ` · ${s.byMode.FEED ?? 0} en défilement, ${s.byMode.STEPS ?? 0} en pas à pas`}</span>
      </div>
      {s.visits === 0 ? <div className="empty">Pas encore de visites pour cette version.</div> : (
        <>
          {skip && skip.skipRate! >= 0.4 && <div className="alert warn small">La carte {skip.index + 1} (« {skip.title} ») est sautée par {pct(skip.skipRate)} des élèves : trop longue ou peu claire ?</div>}
          <table className="t">
            <thead><tr><th>Carte</th><th>Vues</th><th>Temps médian</th><th>Sautée (&lt; 1,5 s)</th><th>Question réussie</th><th>« Pas compris »</th></tr></thead>
            <tbody>
              {s.cards.map((c) => (
                <tr key={c.stepId}>
                  <td><b>{c.index + 1}.</b> {c.title}</td>
                  <td>{c.views}</td>
                  <td>{sec(c.medianDwellMs)}</td>
                  <td style={c.skipRate != null && c.skipRate >= 0.4 ? { color: "var(--amber-text)", fontWeight: 700 } : undefined}>{pct(c.skipRate)}</td>
                  <td style={c.quizSuccess != null && c.quizSuccess < 0.5 ? { color: "var(--amber-text)", fontWeight: 700 } : undefined}>{c.quizAnswered ? `${pct(c.quizSuccess)} (${c.quizAnswered})` : "—"}</td>
                  <td>{c.help || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
